import type { ReactElement } from "react"
import { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import { Grid } from "@mui/material"

import { KonanEmptyState } from "../../../components/utility/KonanEmptyState"
import type { RootState } from "../../../store/ReduxStore"
import { useIsPermitted } from "../../../utils/PermissionsHelpers"
import { AutoMLCreationDialogContainer } from "./AutoMlModelCreationDialog"
import { CreationDialogContainer } from "./CreationDialog"

/**
 * Component for handling initial model creation
 * Displays empty state and manages creation dialogs based on project type
 * @returns {ReactElement}
 */
export function EmptyState(): ReactElement {
  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const isAutoMLProject = currentProject?.type === "credit_scoring"

  // Dialog visibility state
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const { t } = useTranslation("models")

  return (
    <Fragment>
      {/* New Model Dialog */}
      {isDialogOpen &&
        (isAutoMLProject ? (
          <AutoMLCreationDialogContainer isOpen setIsOpen={setIsDialogOpen} />
        ) : (
          <CreationDialogContainer isOpen onClose={() => setIsDialogOpen(false)} />
        ))}

      <Grid container xs={12} className={"empty-container"} width={"100%"} height={"100%"}>
        <KonanEmptyState
          setAction={setIsDialogOpen}
          title={t("empty_state.title")}
          buttonText={
            useIsPermitted("Add model", permissions.konan, flattenedKonanPermissions)
              ? isAutoMLProject
                ? t("empty_state.train_new_model")
                : t("empty_state.deploy_new_model")
              : undefined
          }
          subTitle={isAutoMLProject ? t("empty_state.sub_train_first") : t("empty_state.sub_deploy_first")}
        />
      </Grid>
    </Fragment>
  )
}
